import { useState, useEffect } from 'react';
import { X, Check, RefreshCw } from 'react-feather';
import { agencyService } from '../../services/agency.service';
import { toast } from 'react-hot-toast';
import { Button } from '../ui/button';

interface AgencyCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (code: string) => void;
  firstName: string;
}

export function AgencyCodeModal({
  isOpen,
  onClose,
  onSuccess,
  firstName,
}: AgencyCodeModalProps) {
  const [code, setCode] = useState('');

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError('');
      generateSuggestedCode();
    }
  }, [isOpen, firstName]);

  const generateSuggestedCode = async () => {
    try {
      setIsGeneratingCode(true);
      const response = await agencyService.suggestCode();
      setCode(response.code);
    } catch (err) {
      console.error('Failed to generate code:', err);
      toast.error('Failed to generate agency code');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleCodeChange = (newCode: string) => {
    // Remove spaces and convert to uppercase
    const formattedCode = newCode.replace(/\s+/g, '').toUpperCase();
    setCode(formattedCode);
    setError('');
  };

  const handleSave = async () => {
    if (!code.trim()) {
      toast.error('Please provide a valid code');
      return;
    }

    try {
      setIsLoading(true);
      const response = await agencyService.applyCode({ code });
      onSuccess(response.code || code);
      toast.success('Agency code applied successfully!');
      onClose();
    } catch (err: any) {
      console.error('Apply code error:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to apply code. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold mb-2">Set Your Agency Code</h2>
            <p className="text-muted-foreground text-sm">
              Choose a unique code for your agency to track referrals
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Code Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Your agency code
          </label>
          <div className="relative">
            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                disabled={isLoading || isGeneratingCode}
                className={`flex-1 px-3 py-2 border ${
                  error ? "border-red-500" : "border-border"
                } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 bg-card text-center font-medium text-lg`}
                placeholder="Enter code"
              />
              <button
                onClick={generateSuggestedCode}
                disabled={isLoading || isGeneratingCode}
                className="px-3 py-2 bg-muted hover:bg-accent rounded-lg disabled:opacity-50"
                title="Generate new code"
              >
                {isGeneratingCode ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {isGeneratingCode && (
              <p className="text-muted-foreground text-sm mt-1">
                Generating code...
              </p>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mb-6 p-4 bg-brand-50 rounded-lg">
          <h3 className="font-medium text-brand-900 mb-2">Why do you need a code?</h3>
          <ul className="text-sm text-brand-800 space-y-1">
            <li>• Track referrals from your agency</li>
            <li>• Generate unique referral links</li>
            <li>• Monitor your agency's performance</li>
            <li>• Earn commissions from referred brands</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !code.trim()}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Applying...</span>
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                <span>Apply Code</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
} 